import {Component, DestroyRef, inject, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {
  selectFilteredRulesWithCurrentPage,
  selectIsRulesLoading,
  selectRuleFilter,
  selectRulesTotalCount
} from '../../state/rule.selectors';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Store} from '@ngrx/store';
import {PageEvent} from '@angular/material/paginator';
import {withResetPageOnOtherFilterChange} from '../../../../shared/utils/filter-utils';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {RuleActions} from '../../state/rule.actions';
import {catchError, EMPTY, take} from 'rxjs';
import {CardComponent} from '../../../../shared/components/card/card.component';
import {RuleItemCardComponent} from '../rule-item-card/rule-item-card.component';
import {PaginationComponent} from '../../../../shared/components/pagination/pagination.component';
import {InputWrapperComponent} from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import {TranslatePipe} from '@ngx-translate/core';
import {RouterLink} from '@angular/router';
import {IconComponent} from '../../../../shared/icons/icon.component';
import {AsyncPipe, NgTemplateOutlet} from '@angular/common';
import {RouteConstants} from '../../../../shared/constants/route-constants';
import {selectIsSystemRunning} from '../../../dashboard/state/kill-switch.selectors';
import {MatDialog} from '@angular/material/dialog';
import {FilterDialogComponent} from '../../../../shared/components/filter-dialog/filter-dialog.component';
import {selectAllCisterns} from '../../../cisterns/state/cistern.selectors';
import {CisternActions} from '../../../cisterns/state/cistern.actions';

@Component({
  selector: 'app-rule-list',
  imports: [
    CardComponent,
    RuleItemCardComponent,
    PaginationComponent,
    InputWrapperComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    RouterLink,
    IconComponent,
    AsyncPipe,
    NgTemplateOutlet,
  ],
  templateUrl: './rule-list.component.html',
  styleUrl: './rule-list.component.scss'
})
export class RuleListComponent implements OnInit {
  private _fb = inject(FormBuilder);
  private store = inject(Store);

  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);

  loading$ = this.store.select(selectIsRulesLoading);
  totalCount$ = this.store.select(selectRulesTotalCount);
  ruleList$ = this.store.select(selectFilteredRulesWithCurrentPage);
  killSwitch$ = this.store.select(selectIsSystemRunning)
  @ViewChild('filterFields', {static: true}) filterFieldsTemplate?: TemplateRef<unknown>;
  isFilterDialogOpen = false;

  filterForm: FormGroup
  filterOptions = {
    isActive: [
      {label: 'rules.filter.all', value: null},
      {label: 'rules.filter.inactive', value: false},
      {label: 'rules.filter.active', value: true}
    ]
  }

  constructor() {
    this.filterForm = this._fb.group({
      searchTerm: [null],
      isActive: [null],
      page: [null],
      pageSize: [null],
    });
  }

  onChangePage(p: PageEvent) {
    this.filterForm.controls['page'].patchValue(p.pageIndex);
  }

  ngOnInit() {
    this.store.select(selectAllCisterns).pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((cisterns) => {
      if (cisterns.length === 0) {
        this.store.dispatch(CisternActions.public.loadAllCisternsByFilters({}));
      }
    });

    this.filterForm.valueChanges.pipe(
      withResetPageOnOtherFilterChange(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((newFilter) => {
      this.store.dispatch(RuleActions.public.updateRuleFilter({filter: newFilter}));
    });

    this.store.select(selectRuleFilter).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(_ => EMPTY)
    ).subscribe((filter) => {
      this.filterForm.patchValue({...filter});
    });
  }

  onDeleteRule(id: string): void {
    this.store.dispatch(RuleActions.public.deleteRuleById({id: id}))
  }

  onChangeIsActive(data: { id: string, isActive: boolean }) {
    this.store.dispatch(RuleActions.public.updateIsActive({
      id: data.id,
      isActive: data.isActive,
    }));
  }

  openFilterDialog() {
    if (!this.filterFieldsTemplate || this.isFilterDialogOpen) return;
    this.isFilterDialogOpen = true;

    const dialogRef = this.dialog.open(FilterDialogComponent, {
      width: 'min(34rem, calc(100vw - 2rem))',
      maxWidth: '100vw',
      data: {
        titleKey: 'default.filter',
        contentTemplate: this.filterFieldsTemplate,
      },
    });

    this.filterForm.valueChanges.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => dialogRef.close());

    dialogRef.afterClosed().pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.isFilterDialogOpen = false;
    });
  }

  protected readonly RouteConstants = RouteConstants;
}
